import { CaseConverter } from '../helpers/case-converter.helper';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpAuthHelperService } from './http-auth-helper.service';
import { Form, FormAssigned, FormField } from '../entities/form.entity';
import { StringHelper } from '../helpers/string-helper';
import { FileUploadService } from './file-upload.service';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AdminFormsService {
  private baseUrl = environment.endpoint;

  constructor(
    private fileUploadService: FileUploadService,
    private httpAuthHelperService: HttpAuthHelperService
  ) {}

  async save(form: Form): Promise<any> {
    const formToSave = { ...form };
    if (
      formToSave['isValid'] !== undefined &&
      typeof formToSave['isValid'] === 'boolean'
    ) {
      formToSave['isValid'] = formToSave['isValid'] ? 1 : 0;
    }

    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('POST', `${this.baseUrl}/forms/save-form`, {
          form: CaseConverter.keysToSnakeCase(formToSave),
        })
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(data.body.response);
          }
          resolve(null);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async saveUserForm(
    uuid: string,
    formUUID: string,
    data: any,
    completedCount: any,
    submitted: boolean = false,
    forcedTermination: boolean = false,
    _identifierValue: string | any = null
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('POST', `${this.baseUrl}/forms/save-data`, {
          uuid,
          formUUID,
          completedCount,
          submitted,
          forcedTermination,
          identifierValue: _identifierValue,
          data: CaseConverter.keysToSnakeCase(data),
        })
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          console.log(err);
          reject(err);
        });
    });
  }

  async reOpenForm(userDataUUID: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/re-open-form-by-user-concern`,
          { userDataUUID }
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(data.body.response);
          }
          resolve(null);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async updateStatus(form: Form): Promise<any> {
    const payload = {
      uuid: form.uuid,
      isDisabled: form.isDisabled,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/update-status-form`,
          payload
        )
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async getFormAssets(keys: string[]): Promise<any> {
    const payload = {
      keys,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/get-form-assets`,
          payload
        )
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async getLimitedForms(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('GET', `${this.baseUrl}/forms/limited-forms`)
        .then((data) => {
          if (data && data.body && data.body.response) {
            const response = data.body.response;
            resolve(response);
          }
          resolve([]);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async createLimitedFormEntry(formUUID: string): Promise<any> {
    const payload = {
      formUUID,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/create-limited-form-entry`,
          payload
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            const response = data.body.response;
            resolve(response);
            if (response.uuid) {
              return response.uuid;
            } else {
              reject('An Error Ocurr!');
            }
          }
          reject('An Error Ocurr!');
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async createStandaloneFormEntry(formUUID: string): Promise<any> {
    const payload = {
      formUUID,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/create-standalone-form-entry`,
          payload
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            const response = data.body.response;
            resolve(response);
            if (response.uuid) {
              return response.uuid;
            } else {
              reject('An Error Ocurr!');
            }
          }
          reject('An Error Ocurr!');
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getAll(project: string): Promise<Form[]> {
    if (navigator.onLine) {
      return this.get(project);
    } else {
      return Promise.resolve([]);
    }
  }

  async HARD_DELETE_FormHistory(
    uuid: string,
    parentUUID: any,
    onProgress: (progress: number) => void
  ): Promise<any> {
    const payload = {
      uuid,
      parent_uuid: parentUUID,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestWithProgressPromise(
          'POST',
          `${this.baseUrl}/forms/delete-form-history`,
          onProgress,
          payload
        )
        .then((data) => {
          resolve(CaseConverter.keysToCamelCase(data));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  private get(project: string): Promise<Form[]> {
    return new Promise<Form[]>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/get-forms`,
          { project: project },
          null
        )
        .then((data) => {
          if (data && data.body.response && data.body.response.length > 0) {
            const forms = CaseConverter.keysToCamelCase(data.body.response, [
              'table',
              'settings',
            ]);
            resolve(forms as Form[]);
          }
          resolve([]);
        })
        .catch((err) => {
          console.log(err);
          reject(err);
        });
    });
  }

  isValidField(field: FormField): boolean {
    field.errors = [];
    field.isValid = false;
    if (
      !field.order ||
      !field.fieldId ||
      !field.fieldLabel ||
      field.isRequired === null ||
      field.isRequired === undefined ||
      !field.type
    ) {
      field.errors.push('Missing required fields');
      return false;
    }

    if (
      ['single-choose', 'multiple-choose', 'ranking'].includes(field.type) &&
      (!field.options ||
        field.options.length <= 1 ||
        !field.options.every((option) => option.trim() !== ''))
    ) {
      field.errors.push('At least two options are required');
      return false;
    }

    if (
      ['satisfactory-scale'].includes(field.type) &&
      (!field.metadata ||
        !field.metadata.maxRanking ||
        field.metadata.maxRanking > 10)
    ) {
      return false;
    }

    if (
      field.metadata?.useFollowUpQuestion &&
      field.metadata?.followUpTriggerType === ''
    ) {
      field.errors.push('Incomplete followup trigger configuration');
      return false;
    }

    if (field.metadata.useScores) {
      if (['single-choose', 'multiple-choose', 'yesno'].includes(field.type)) {
        const scores = field.scores.filter(
          (score) =>
            StringHelper.isNumber(score) && score >= -1 && score !== null
        );
        if (scores.length < field.options.length) {
          field.errors.push('Scores are incomplete');
          return false;
        }
      } else if (
        field.type === 'free-text' &&
        field.metadata.textDataType === 'numeric'
      ) {
        if (!StringHelper.isNumber(field.metadata.maxScore)) {
          field.errors.push('Max Score is required');
          return false;
        }
      }
    }

    if (
      field.metadata?.useFollowUpQuestion &&
      ['single-opt', 'multiple-opt'].includes(
        field.metadata?.followUpTriggerType || ''
      )
    ) {
      if (
        field.metadata.followUpTriggerType === 'single-opt' &&
        !field.metadata.followUpTriggerValue?.length
      ) {
        field.errors.push('Incomplete followup trigger configuration');
        return false;
      }

      if (
        field.metadata.followUpTriggerType === 'multiple-opt' &&
        !field.triggers?.length
      ) {
        field.errors.push('Incomplete followup trigger configuration');
        return false;
      }
    }

    if (field.type === 'file-upload') {
      if (
        !field.metadata.acceptedExtensions ||
        field.metadata?.acceptedExtensions?.length === 0
      ) {
        field.errors.push('Accepted extensions are required');
        return false;
      }
    }
    field.isValid = true;
    return true;
  }

  getAssignmentForms(): Promise<{
    assigned: FormAssigned[];
  }> {
    return new Promise<{ assigned: FormAssigned[]; submitted: FormAssigned[] }>(
      (resolve, reject) => {
        this.httpAuthHelperService
          .requestPromise(
            'POST',
            `${this.baseUrl}/forms/get-assigned-forms`,
            null
          )
          .then((data) => {
            if (data && data.body && data.body.response) {
              const response = data.body.response;
              const forms = CaseConverter.keysToCamelCase(response, ['table']);
              resolve(forms);
            }
            resolve({
              assigned: [] as FormAssigned[],
              submitted: [] as FormAssigned[],
            });
          })
          .catch((err) => {
            reject(err);
          });
      }
    );
  }
  getStandaloneFormProgress(formUUID: string): Promise<{
    assigned: FormAssigned;
  }> {
    return new Promise<{ assigned: FormAssigned; submitted: FormAssigned[] }>(
      (resolve, reject) => {
        this.httpAuthHelperService
          .requestPromise(
            'POST',
            `${this.baseUrl}/forms/get-standalone-progress`,
            { formUUID }
          )
          .then((data) => {
            if (data && data.body && data.body.response) {
              const response = data.body.response;
              const forms = CaseConverter.keysToCamelCase(response, ['table']);
              resolve(forms);
            }
            reject('An Error Ocurr!');
          })
          .catch((err) => {
            reject(err);
          });
      }
    );
  }

  getSubmittedForms(): Promise<{
    submitted: FormAssigned[];
  }> {
    return new Promise<{ assigned: FormAssigned[]; submitted: FormAssigned[] }>(
      (resolve, reject) => {
        this.httpAuthHelperService
          .requestPromise(
            'POST',
            `${this.baseUrl}/forms/get-submitted-forms`,
            null
          )
          .then((data) => {
            if (data && data.body && data.body.response) {
              const response = data.body.response;
              const forms = CaseConverter.keysToCamelCase(response, ['table']);
              resolve(forms);
            }
            resolve({
              assigned: [] as FormAssigned[],
              submitted: [] as FormAssigned[],
            });
          })
          .catch((err) => {
            reject(err);
          });
      }
    );
  }

  async getSignedUrl(key: string, mimeType: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('POST', `${this.baseUrl}/forms/create-signed-link`, {
          key: key,
          mimeType: mimeType,
        })
        .then((data) => {
          if (data && data.body && data.body.response) {
            const response = data.body.response;
            resolve(response.signedUrl);
          }
          reject('An Error Ocurr!');
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async checkArtifactExists(key: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('POST', `${this.baseUrl}/forms/check-artifact-export`, {
          key: key,
        })
        .then((data) => {
          if (data && data.body && data.body.response) {
            const response = data.body.response;
            resolve(response);
          }
          reject('An Error Ocurr!');
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
  async getArtifactFile(key: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/get-artifact-signed-link`,
          {
            key: key,
          }
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            const response = data.body.response;
            resolve(response.signedUrl);
          }
          reject('An Error Ocurr!');
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async getArtifactExportSignedUrl(
    key: string,
    mimeType: string,
    formUUID: string = '-1'
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/create-artifact-export-signed-link`,
          {
            key: key,
            mimeType: mimeType,
            formUUID: formUUID,
          }
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            const response = data.body.response;
            resolve(response.signedUrl);
          }
          reject('An Error Ocurr!');
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async uploadArtifactExportViaSignedLink(
    key: string,
    file: File,
    formUUID: string,
    onProgress: (progress: number) => void
  ): Promise<any> {
    try {
      const signedUrl = await this.getArtifactExportSignedUrl(
        key,
        file.type,
        formUUID
      );
      if (!signedUrl) {
        throw new Error('Signed URL is undefined or empty.');
      }
      await lastValueFrom(
        this.fileUploadService.uploadFileToS3(signedUrl, file, onProgress)
      );
    } catch (err) {
      console.error('Upload failed:', err);
      throw new Error('Upload initialization failed');
    }
  }

  async uploadAssetViaSignedLink(
    key: string,
    file: File,
    onProgress: (progress: number) => void
  ): Promise<any> {
    try {
      const signedUrl = await this.getSignedUrl(key, file.type);
      if (!signedUrl) {
        throw new Error('Signed URL is undefined or empty.');
      }
      await lastValueFrom(
        this.fileUploadService.uploadFileToS3(signedUrl, file, onProgress)
      );
    } catch (err) {
      console.error('Upload failed:', err);
      throw new Error('Upload initialization failed');
    }
  }

  async uploadAsset(
    key: string,
    buffer: any,
    mimeType: string,
    onProgress: (progress: number) => void,
    options?: { replicateOnBucket?: string; bucketPrefix?: string }
  ): Promise<any> {
    const replicateBucket = options?.replicateOnBucket?.trim();
    const prefix = options?.bucketPrefix?.trim();
    const payload: {
      key: string;
      base64File: any;
      mimeType: string;
      settings?: { replicateOnBucket?: string; bucketPrefix?: string };
    } = {
      key,
      base64File: buffer,
      mimeType,
    };
    const settings: { replicateOnBucket?: string; bucketPrefix?: string } = {};
    if (replicateBucket) {
      settings.replicateOnBucket = replicateBucket;
    }
    if (prefix) {
      settings.bucketPrefix = prefix;
    }
    if (Object.keys(settings).length > 0) {
      payload.settings = settings;
    }
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestWithProgressPromise(
          'POST',
          `${this.baseUrl}/forms/create-form-assets`,
          onProgress,
          payload
        )
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async getUserSubmittedFormReferences(sourceType: string): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/get-user-submitted-form-references`,
          {
            sourceType,
          }
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            const response = data.body.response;
            resolve(response);
          }
          reject('An Error Ocurr, Getting User Submitted Form References');
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
  async getUserFormByUserDataUUID(userDataUUID: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/forms/get-user-form-data-detail`,
          {
            userDataUUID,
          }
        )
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(CaseConverter.keysToCamelCase(data.body.response));
          }
          resolve(null);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async migrateUserFormData(formUUID: string, userForms: any[]): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('POST', `${this.baseUrl}/forms/migrate-user-forms`, {
          newFormUUID: formUUID,
          userForms,
        })
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async getAffectedUserByFormUpdate(formUUID: string): Promise<any[]> {
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('POST', `${this.baseUrl}/forms/get-affected-users`, {
          formUUID,
        })
        .then((data) => {
          if (data && data.body && data.body.response) {
            resolve(data.body.response);
          }
          resolve([]);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
}
